import { PatientOrderStatus } from '../pages/Patients/PatientDashboard/Patient.model'
import { PatientStatus } from '../constants/patient'
import config from '../services/ConfigProvider'
import { IConfig } from '../types/IConfig'
import { PatientDetailsDto } from '../types/swaggerTypes'

export type CastorEdcConfiguration = Pick<
  IConfig,
  'castorEdcBaseUrl' | 'castorEdcRewriteSourceHost' | 'castorEdcIdpHint'
>

const CASTOR_EDC_AUTHENTICATE_PATH = '/castor-identity/authenticate'
const CASTOR_EDC_DEEP_LINK_IDP_HINT_PARAMETER = 'kc_idp_hint'
const CASTOR_EDC_AUTH_LOGIN_HINT_PARAMETER = 'login_hint'
const CASTOR_EDC_AUTH_IDP_HINT_PARAMETER = 'idp_hint'

const SHIPPED_OR_DELIVERED_ORDER_STATUSES = [
  PatientOrderStatus.Shipped,
  PatientOrderStatus.InTransit,
  PatientOrderStatus.OutForDelivery,
  PatientOrderStatus.Delivered,
  PatientOrderStatus.ReadyForResupply,
  PatientOrderStatus.FailedAttempt,
  PatientOrderStatus.FailedDelivery,
  PatientOrderStatus.Available_for_Pickup
]

const normalizeOrderStatus = (status?: string | null): string => {
  if (!status) {
    return ''
  }

  return status.replace(/_/g, ' ').trim().toLowerCase()
}

const shippedOrDeliveredNormalized = new Set(
  SHIPPED_OR_DELIVERED_ORDER_STATUSES.map(normalizeOrderStatus)
)

export const isShippedOrDeliveredOrderStatus = (
  status?: string | null
): boolean => shippedOrDeliveredNormalized.has(normalizeOrderStatus(status))

export const patientHasShippedOrder = (
  patient?: Pick<PatientDetailsDto, 'patientOrderStatus' | 'pastOrders'> | null
): boolean => {
  if (!patient) {
    return false
  }

  if (isShippedOrDeliveredOrderStatus(patient.patientOrderStatus)) {
    return true
  }

  return (
    patient.pastOrders?.some((order) =>
      isShippedOrDeliveredOrderStatus(order.orderStatus)
    ) ?? false
  )
}

const isDiscontinuedPatient = (
  patient: Pick<PatientDetailsDto, 'patientOrderStatus' | 'patientStatus'>
): boolean =>
  normalizeOrderStatus(patient.patientOrderStatus) ===
    normalizeOrderStatus(PatientOrderStatus.PatientDiscontinued) ||
  normalizeOrderStatus(patient.patientStatus) ===
    normalizeOrderStatus(PatientStatus.DISCONTINUED)

/**
 * Follow-up data is asked for once an order has shipped and stays until the
 * patient is discontinued, whatever the status of a later order is (the Submit
 * follow up data card used to disappear while a second order had not shipped).
 */
export const canSubmitFollowUpData = (
  patient?: Pick<
    PatientDetailsDto,
    'patientOrderStatus' | 'patientStatus' | 'pastOrders'
  > | null
): boolean =>
  !!patient &&
  patientHasShippedOrder(patient) &&
  !isDiscontinuedPatient(patient)

const getValidHttpsUrl = (url?: string | null): URL | undefined => {
  const trimmed = url?.trim()

  if (!trimmed) {
    return undefined
  }

  try {
    const parsed = new URL(trimmed)

    if (
      parsed.protocol !== 'https:' ||
      parsed.username.length > 0 ||
      parsed.password.length > 0
    ) {
      return undefined
    }

    return parsed
  } catch {
    return undefined
  }
}

/** Returns the environment-specific Castor EDC base URL supplied at runtime. */
export const getCastorEdcHomepageUrl = (
  castorConfig: CastorEdcConfiguration = config
): string => getValidHttpsUrl(castorConfig.castorEdcBaseUrl)?.toString() ?? ''

/**
 * CLOS-13950: Oracle may return an EDC link for a different Castor environment.
 * Rewrite only the configured source host and preserve path, query, and fragment.
 */
export const rewriteCastorEdcUrlForConfiguredHost = (
  url: string,
  castorConfig: CastorEdcConfiguration = config
): string => {
  const sourceHost = castorConfig.castorEdcRewriteSourceHost
    ?.trim()
    .toLowerCase()
  const configuredBaseUrl = getValidHttpsUrl(castorConfig.castorEdcBaseUrl)

  if (!sourceHost || !configuredBaseUrl) {
    return url
  }

  try {
    const parsed = new URL(url)

    if (parsed.hostname.toLowerCase() === sourceHost) {
      parsed.protocol = configuredBaseUrl.protocol
      parsed.host = configuredBaseUrl.host
      return parsed.toString()
    }
  } catch {
    return url
  }

  return url
}

// CLOS-13743: PTA patients may not yet have a patient-specific eConsent deep link
// when their record first lands in Clinigen Direct. Use a configurable fallback so
// the "Capture patient consent" button is always enabled and redirects the user to
// the Castor eConsent SSO entry point on the first click.
// Host must be eu.castorconsent.com (clinigen.castorconsent.com does not resolve).
export const CASTOR_ECONSENT_HOMEPAGE_URL = 'https://eu.castorconsent.com/'

export const getValidCastorEdcUrl = (
  url?: string | null,
  castorConfig: CastorEdcConfiguration = config
): string | undefined => {
  const parsed = getValidHttpsUrl(url)

  if (!parsed) {
    return undefined
  }

  return rewriteCastorEdcUrlForConfiguredHost(parsed.toString(), castorConfig)
}

// CLOS-14021: Castor eConsent/EDC deep links from Oracle/OIC carry no IdP hint.
export const getCastorIdpHint = (
  castorConfig: CastorEdcConfiguration = config
): string => castorConfig.castorEdcIdpHint?.trim() ?? ''

// Adds the configured deep-link IdP hint if absent and preserves query/fragment data.
export const appendCastorIdpHint = (
  url: string,
  castorConfig: CastorEdcConfiguration = config
): string => {
  const idpHint = getCastorIdpHint(castorConfig)

  if (!idpHint) {
    return url
  }

  try {
    const parsed = new URL(url)

    if (!parsed.searchParams.has(CASTOR_EDC_DEEP_LINK_IDP_HINT_PARAMETER)) {
      parsed.searchParams.set(CASTOR_EDC_DEEP_LINK_IDP_HINT_PARAMETER, idpHint)
    }

    return parsed.toString()
  } catch {
    return url
  }
}

/**
 * Returns the deep-link URL for a patient record in Castor EDC when available,
 * falling back to the configured Castor EDC homepage when no valid
 * patient deep-link exists. Appends the configured Clinigen IdP hint (CLOS-14021).
 */
export const getCastorEdcUrl = (
  url?: string | null,
  castorConfig: CastorEdcConfiguration = config
): string =>
  appendCastorIdpHint(
    getValidCastorEdcUrl(url, castorConfig) ??
      getCastorEdcHomepageUrl(castorConfig),
    castorConfig
  )

/**
 * Builds Castor's trusted EDC authentication endpoint for the signed-in user.
 * The email is deliberately sent only to the configured Castor host,
 * never to the patient deep-link host supplied by Oracle/OIC.
 */
export const getCastorEdcAuthenticationUrl = (
  userEmail?: string | null,
  castorConfig: CastorEdcConfiguration = config,
  castorEdcUrl?: string | null
): string | undefined => {
  const normalizedEmail = userEmail?.trim()
  const baseUrl = getValidHttpsUrl(castorConfig.castorEdcBaseUrl)
  const idpHint = getCastorIdpHint(castorConfig)

  if (!normalizedEmail || !baseUrl || !idpHint) {
    return undefined
  }

  const destination = getValidHttpsUrl(
    getCastorEdcUrl(castorEdcUrl, castorConfig)
  )
  // Only transplant paths belonging to the configured Castor environment.
  if (!destination || destination.origin !== baseUrl.origin) return undefined

  const authenticationUrl = new URL(CASTOR_EDC_AUTHENTICATE_PATH, baseUrl)
  authenticationUrl.searchParams.set(
    CASTOR_EDC_AUTH_LOGIN_HINT_PARAMETER,
    normalizedEmail
  )
  authenticationUrl.searchParams.set(
    CASTOR_EDC_AUTH_IDP_HINT_PARAMETER,
    idpHint
  )

  authenticationUrl.searchParams.set(
    'deeplink',
    `${destination.pathname}${destination.search}${destination.hash}`
  )

  return authenticationUrl.toString()
}

/** Castor EDC study landing URL when only the programme country study ID is available. */
export const getCastorEdcUrlFromStudyId = (
  studyId?: string | null,
  castorConfig: CastorEdcConfiguration = config
): string => {
  const homepage = getCastorEdcHomepageUrl(castorConfig)
  const trimmed = studyId?.trim()

  if (!homepage || !trimmed) {
    return appendCastorIdpHint(homepage, castorConfig)
  }

  const base = homepage.replace(/\/$/, '')
  return appendCastorIdpHint(
    `${base}/study/${encodeURIComponent(trimmed)}`,
    castorConfig
  )
}

/**
 * Returns the deep-link URL for a patient record in Castor eConsent when available,
 * falling back to the Castor eConsent homepage when no valid patient deep-link exists.
 * Appends the configured Clinigen IdP hint (CLOS-14021).
 */
export const getCastorEConsentUrl = (url?: string | null): string =>
  appendCastorIdpHint(
    getValidHttpsUrl(url)?.toString() ?? CASTOR_ECONSENT_HOMEPAGE_URL
  )

export const openExternalUrl = (url: string): void => {
  if (!url.trim()) {
    return
  }

  window.open(url, '_blank', 'noopener,noreferrer')
}

/** Keep the existing direct-link fallback when SSO configuration/email is absent. */
export const getCastorEdcLaunchUrl = (
  castorEdcUrl?: string | null,
  userEmail?: string | null,
  castorConfig: CastorEdcConfiguration = config
): string =>
  getCastorEdcAuthenticationUrl(userEmail, castorConfig, castorEdcUrl) ??
  getCastorEdcUrl(castorEdcUrl, castorConfig)

/** One click: Castor authenticates and then routes to the supplied record. */
export const openCastorEdcUrl = (
  castorEdcUrl?: string | null,
  userEmail?: string | null
): void => {
  openExternalUrl(getCastorEdcLaunchUrl(castorEdcUrl, userEmail))
}

/**
 * Opens a tab inside the click gesture, then navigates it once the URL resolves.
 * Falls back to fallbackUrl when resolution fails.
 */
export const openExternalUrlWhenResolved = (
  resolveUrl: () => Promise<string>,
  fallbackUrl: string
): Promise<void> => {
  const externalWindow = window.open('', '_blank')

  if (externalWindow) {
    externalWindow.opener = null
  }

  return resolveUrl()
    .catch(() => fallbackUrl)
    .then((url) => {
      if (!url.trim()) {
        return
      }

      if (externalWindow) {
        externalWindow.location.replace(url)
        return
      }

      openExternalUrl(url)
    })
}

/** Async record navigation when the dashboard must fetch the patient deep link. */
export const openCastorEdcUrlWhenResolved = (
  resolveUrl: () => Promise<string>,
  fallbackUrl: string,
  userEmail?: string | null
): Promise<void> =>
  openExternalUrlWhenResolved(
    () => resolveUrl().then((url) => getCastorEdcLaunchUrl(url, userEmail)),
    getCastorEdcLaunchUrl(fallbackUrl, userEmail)
  )
